import { motion } from "framer-motion";
import { BookOpen, Flame, ChefHat, BookMarked } from "lucide-react";
import { IMAGES } from "@/brand";

const EASE = [0.22, 1, 0.36, 1];

const HIGHLIGHTS = [
    { icon: Flame, text: "Family jerk recipes, written down properly for the first time" },
    { icon: ChefHat, text: "Curtis's own spice matrix — how to build heat without losing flavour" },
    { icon: BookMarked, text: "Hardcover, made to live next to the stove, not on a shelf" },
];

export const Cookbook = () => (
    <section id="cookbook" className="relative overflow-hidden bg-coal py-28 sm:py-36" data-testid="cookbook-section">
        <div className="absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-bonnet/10 blur-[140px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE }}
            >
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-bonnetglow">From Curtis's kitchen</p>
                <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-parchment sm:text-5xl lg:text-6xl">
                    Master Chef <span className="font-editorial font-medium normal-case italic text-nutmeg">Guide</span>
                </h2>
                <span
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-nutmeg/50 bg-nutmeg/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-nutmeg"
                    data-testid="cookbook-badge-coming-soon"
                >
                    <BookOpen className="h-3.5 w-3.5" />
                    Hardcover · Coming soon
                </span>
                <p className="mt-8 text-base leading-relaxed text-sand sm:text-lg">
                    The SpiceJam cookbook gathers the recipes behind the jars — the jerk Curtis grew up with, the
                    BBQ smoke he perfected in Ontario, and every ratio, rest time and scotch bonnet warning in
                    between. It's coming soon, and launch-list friends hear first.
                </p>
                <ul className="mt-10 space-y-5">
                    {HIGHLIGHTS.map((h, i) => (
                        <motion.li
                            key={h.text}
                            initial={{ opacity: 0, x: -24 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, ease: EASE, delay: 0.15 + i * 0.12 }}
                            className="flex items-start gap-4"
                        >
                            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-nutmeg/30 bg-nutmeg/10">
                                <h.icon className="h-4 w-4 text-nutmeg" />
                            </span>
                            <span className="text-base leading-relaxed text-sand">{h.text}</span>
                        </motion.li>
                    ))}
                </ul>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 48, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: EASE }}
                className="relative mx-auto w-full max-w-md"
                data-testid="cookbook-card"
            >
                <div className="absolute -inset-4 rounded-[2rem] border border-nutmeg/20" />
                <div className="relative overflow-hidden rounded-3xl border border-white/15 shadow-[0_48px_96px_-32px_rgba(0,0,0,0.85)] transition-transform duration-700 ease-out hover:rotate-0">
                    <img
                        src={IMAGES.cookbook}
                        alt="Master Chef Guide — Curtis's SpiceJam cookbook, hardcover"
                        className="h-[520px] w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-obsidian/20" />
                    <div className="absolute bottom-6 left-6 right-6">
                        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-nutmeg">SpiceJam Cookbook</p>
                        <p className="mt-1 font-editorial text-2xl italic text-parchment">Master Chef Guide</p>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

export default Cookbook;
